/**
 * Insights frequency capping
 * @module lib/insights/frequencyCap
 *
 * Completion is capped per survey, not brand-wide: completing survey X marks
 * X as done for this browser (localStorage), and X is never served to it
 * again — a repeat response to the same survey would be flagged as a COOKIE
 * duplicate server-side anyway. Other surveys stay eligible immediately, and
 * the completed ids are sent to the allocator as an exclusion list so the
 * embed slot serves a different survey instead of going blank. Dismissals do
 * not suppress — viewability is already the report denominator. Contests are
 * never capped.
 */

const COMPLETION_MESSAGE_TYPE = 'MILLA_INSIGHTS_COMPLETED';
// Allocator exclusion lists stay bounded so the /api/embed/active URL cannot
// grow without limit for prolific respondents (newest completions win).
const MAX_EXCLUDED_SURVEY_IDS = 50;

let completionListenerInstalled = false;

function normalizeBrand(brand) {
  return brand === 'mtlblog' ? 'mtlblog' : 'narcity';
}

function surveyKey(brand, surveyId) {
  return `milla_insights_completed:${normalizeBrand(brand)}:${String(surveyId || '').trim()}`;
}

function surveyKeyPrefix(brand) {
  return `milla_insights_completed:${normalizeBrand(brand)}:`;
}

function predictionKey(brand, predictionId) {
  return `milla_prediction_completed:${normalizeBrand(brand)}:${String(predictionId || '').trim()}`;
}

function markInsightsCompleted(brand, surveyId, now = Date.now()) {
  if (typeof window === 'undefined' || !surveyId) return;
  try {
    window.localStorage?.setItem(surveyKey(brand, surveyId), String(now));
  } catch (_) {
    // ignore storage failures
  }
}

function isInsightsFrequencyCapped(brand, surveyId) {
  if (typeof window === 'undefined' || !surveyId) return false;
  try {
    return Boolean(window.localStorage?.getItem(surveyKey(brand, surveyId)));
  } catch (_) {
    return false;
  }
}

// Completed survey ids for this brand, newest first, capped — passed to the
// allocator so it can pick a survey the respondent has not answered yet.
function listCompletedInsightsSurveyIds(brand) {
  if (typeof window === 'undefined') return [];
  try {
    const storage = window.localStorage;
    if (!storage) return [];
    const prefix = surveyKeyPrefix(brand);
    const completed = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key || !key.startsWith(prefix)) continue;
      const surveyId = key.slice(prefix.length);
      if (!surveyId) continue;
      completed.push({ surveyId, completedAt: Number(storage.getItem(key)) || 0 });
    }
    return completed
      .sort((a, b) => b.completedAt - a.completedAt)
      .slice(0, MAX_EXCLUDED_SURVEY_IDS)
      .map((entry) => entry.surveyId);
  } catch (_) {
    return [];
  }
}

function markPredictionCompleted(brand, predictionId, now = Date.now()) {
  if (typeof window === 'undefined' || !predictionId) return;
  try {
    window.localStorage?.setItem(predictionKey(brand, predictionId), String(now));
  } catch (_) {
    // ignore storage failures
  }
}

function isPredictionFrequencyCapped(brand, predictionId) {
  if (typeof window === 'undefined' || !predictionId) return false;
  try {
    return Boolean(window.localStorage?.getItem(predictionKey(brand, predictionId)));
  } catch (_) {
    return false;
  }
}

// The completion signal comes from the survey iframe via postMessage on a
// successful submit.
function ensureInsightsCompletionListener() {
  if (typeof window === 'undefined' || completionListenerInstalled) return;
  completionListenerInstalled = true;
  window.addEventListener('message', (event) => {
    const data = event && event.data;
    if (!data || data.type !== COMPLETION_MESSAGE_TYPE) return;
    const brand = normalizeBrand(data.brand);
    if (data.surveyType === 'PREDICTION' && data.predictionId) {
      markPredictionCompleted(brand, data.predictionId);
      return;
    }
    markInsightsCompleted(brand, data.surveyId);
  });
}

module.exports = {
  MAX_EXCLUDED_SURVEY_IDS,
  ensureInsightsCompletionListener,
  isInsightsFrequencyCapped,
  listCompletedInsightsSurveyIds,
  markInsightsCompleted,
  isPredictionFrequencyCapped,
  markPredictionCompleted
};
